import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';
import { toast } from 'react-toastify';

import { ClinicalStatus } from '../../../../../admin/constants/general';
import { TREATMENT_PLAN_BASE_PATH } from '../../../../../constants';
import { areUserRolesIn, history } from '../../../../../helpers';
import { clientActions, providerActions, treatmentPlanActions } from '../../../../../store';
import { ALL_CLIENTS_ROLES, PA_EXPIRES_WITHIN_DAYS } from '../constants';
import useTreatmentPlanV3HeaderState from './useTreatmentPlanV3HeaderState';

import type { AppDispatch } from '../../../../../store';

export default function useFetchTreatmentPlanV3Header() {
  const dispatch = useDispatch<AppDispatch>();
  const { getAccessTokenSilently } = useAuth0();
  const { treatmentPlanId } = useParams<'treatmentPlanId'>();
  const { user, clients, selectedClient, treatmentPlan, treatmentPlans, isCurrentTpLoading } =
    useTreatmentPlanV3HeaderState();
  // The token getter is read through a ref so the mount/unmount effect runs exactly once.
  const getTokenRef = useRef(getAccessTokenSilently);
  getTokenRef.current = getAccessTokenSilently;
  const requestedPlanIdRef = useRef<string | null>(null);
  const userRoles = user?.roles;
  const planClientId = treatmentPlan?.client_id;
  const hasPlans = treatmentPlans.length > 0;
  const hasSelectedClient = Boolean(selectedClient);
  const selectedClientId = selectedClient?.id;

  useEffect(() => {
    dispatch(providerActions.getProvider({ getAccessTokenSilently: getTokenRef.current }));
    dispatch(
      treatmentPlanActions.getTreatmentPlanStatuses({
        getAccessTokenSilently: getTokenRef.current,
      }),
    );
    return () => {
      dispatch(clientActions.clearSelectedClient());
      dispatch(treatmentPlanActions.clearTreatmentPlan());
      dispatch(treatmentPlanActions.clearTreatmentPlanState());
    };
  }, [dispatch]);

  useEffect(() => {
    if (!userRoles) return;
    if (areUserRolesIn(userRoles, ALL_CLIENTS_ROLES)) {
      dispatch(
        clientActions.getList({
          getAccessTokenSilently: getTokenRef.current,
          clinical_status: [
            ClinicalStatus.CLINICAL,
            ClinicalStatus.OFFBOARDED,
            ClinicalStatus.ON_HOLD,
          ],
        }),
      );
      return;
    }
    dispatch(clientActions.getList({ getAccessTokenSilently: getTokenRef.current }));
  }, [dispatch, userRoles]);

  // The PA expiration tag in the page header reads this list; legacy fetched it the same way.
  useEffect(() => {
    if (selectedClientId === undefined) return;
    dispatch(
      treatmentPlanActions.getTPPriorAuthorizations({
        getAccessTokenSilently: getTokenRef.current,
        pa_expires_within_days: PA_EXPIRES_WITHIN_DAYS,
        client_id: selectedClientId,
      }),
    );
  }, [dispatch, selectedClientId]);

  // Each route change gets one request, so a rejected request does not loop but can retry later.
  useEffect(() => {
    requestedPlanIdRef.current = null;
  }, [treatmentPlanId]);

  useEffect(() => {
    if (!treatmentPlanId) return;
    const parsedId = Number(treatmentPlanId);
    if (!Number.isInteger(parsedId)) {
      if (!hasPlans && hasSelectedClient) {
        toast.error('Please create a treatment plan');
      } else if (hasSelectedClient) {
        toast.error(`Treatment plan with id ${treatmentPlanId} not found`);
      } else {
        toast.error('Please select a client');
      }
      history.navigate(TREATMENT_PLAN_BASE_PATH);
      return;
    }
    if (treatmentPlan?.id === parsedId || isCurrentTpLoading) return;
    if (requestedPlanIdRef.current === treatmentPlanId) return;
    requestedPlanIdRef.current = treatmentPlanId;
    // A plan from another route must not stay visible or editable while this one loads.
    if (treatmentPlan) dispatch(treatmentPlanActions.updateState({ key: 'current', value: null }));
    dispatch(
      treatmentPlanActions.getTreatmentPlanById({
        treatmentPlanId,
        getAccessTokenSilently: getTokenRef.current,
      }),
    );
  }, [dispatch, treatmentPlanId, treatmentPlan, isCurrentTpLoading, hasPlans, hasSelectedClient]);

  useEffect(() => {
    if (planClientId === undefined) return;
    const planClient = clients.find((client) => client.id === planClientId) ?? treatmentPlan?.client;
    if (planClient) dispatch(clientActions.setSelected(planClient));
  }, [clients, dispatch, planClientId, treatmentPlan?.client]);
}
